const REVEAL_ITEM_SELECTORS = ['.product-grid__item', '.resource-list__item'];
const REVEAL_HEADER_SELECTORS = ['.section-resource-list__header', '.main-collection-grid__title'];
const REVEAL_CONTAINER_SELECTORS = ['.product-grid', '.resource-list'];
const REVEAL_CLASS = 'scroll-reveal';
const REVEALED_CLASS = 'is-revealed';
const MAX_STAGGER_STEPS = 8;
const BASE_REVEAL_DELAY_MS = 110;
const STEP_REVEAL_DELAY_MS = 70;
const FORCE_REVEAL_DELAY_MS = 1100;
const COLLECTION_ENTRY_KEY = 'premium-collection-entry';

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
document.documentElement.classList.add('scroll-reveal-enabled');
document.documentElement.classList.add('premium-motion-enabled');

let revealObserver = null;
let mutationObserver = null;
let scanFrame = null;
let forceRevealTimer = null;

const shouldSkipRevealElement = (element) => {
  return element instanceof HTMLElement && Boolean(element.closest('product-recommendations'));
};

const getRevealType = (element) => {
  if (!(element instanceof HTMLElement)) return 'default';
  if (element.matches('.section-resource-list__header, .main-collection-grid__title')) return 'header';
  if (element.matches('.product-grid__item')) return 'product-card';
  if (element.matches('.resource-list__item')) return 'collection-card';
  return 'default';
};

const getRevealDelay = (element) => {
  const staggerIndex = Number.parseInt(element.style.getPropertyValue('--scroll-reveal-index') || '0', 10) || 0;
  const revealType = getRevealType(element);

  if (revealType === 'header') return 40;
  if (revealType === 'collection-card') {
    return 70 + staggerIndex * 50;
  }

  return BASE_REVEAL_DELAY_MS + staggerIndex * STEP_REVEAL_DELAY_MS;
};

const getRevealTargets = (root, selector) => {
  if (!root || typeof root.querySelectorAll !== 'function') return [];
  return [...root.querySelectorAll(selector)].filter((element) => element instanceof HTMLElement);
};

const markRevealed = (element) => {
  element.classList.add(REVEALED_CLASS);
};

const isElementNearViewport = (element) => {
  if (!(element instanceof HTMLElement)) return false;

  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const topThreshold = viewportHeight * 1.08;
  const bottomThreshold = viewportHeight * -0.18;

  return rect.top <= topThreshold && rect.bottom >= bottomThreshold;
};

const revealVisiblePreparedElements = () => {
  document.querySelectorAll(`.${REVEAL_CLASS}:not(.${REVEALED_CLASS})`).forEach((element) => {
    if (element instanceof HTMLElement && isElementNearViewport(element)) {
      scheduleReveal(element);
    }
  });
};

const scheduleForceReveal = () => {
  window.clearTimeout(forceRevealTimer);
  forceRevealTimer = window.setTimeout(() => {
    revealVisiblePreparedElements();
  }, FORCE_REVEAL_DELAY_MS);
};

const markPageReady = () => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.documentElement.classList.add('premium-motion-ready');
    });
  });
};

const shouldAnimateNavigation = (link) => {
  if (!(link instanceof HTMLAnchorElement)) return false;
  if (link.target && link.target !== '_self') return false;
  if (link.hasAttribute('download')) return false;
  if (link.getAttribute('href')?.startsWith('#')) return false;

  const url = new URL(link.href, window.location.href);
  if (url.origin !== window.location.origin) return false;
  if (url.pathname === window.location.pathname && url.search === window.location.search) return false;

  return true;
};

const isCollectionNavigationLink = (link) => {
  if (!(link instanceof HTMLAnchorElement)) return false;

  const url = new URL(link.href, window.location.href);
  return url.origin === window.location.origin && url.pathname.includes('/collections/') && url.pathname !== '/collections/all';
};

const scheduleReveal = (element) => {
  if (!(element instanceof HTMLElement) || element.dataset.scrollRevealScheduled === 'true') return;

  element.dataset.scrollRevealScheduled = 'true';
  const delay = getRevealDelay(element);

  window.setTimeout(() => {
    markRevealed(element);
  }, delay);
};

const prepareRevealElement = (element, index = 0) => {
  if (!(element instanceof HTMLElement) || element.dataset.scrollRevealPrepared === 'true' || shouldSkipRevealElement(element)) {
    return;
  }

  element.dataset.scrollRevealPrepared = 'true';
  element.style.setProperty('--scroll-reveal-index', `${index % MAX_STAGGER_STEPS}`);
  element.classList.add(REVEAL_CLASS);

  if (prefersReducedMotion.matches) {
    markRevealed(element);
    return;
  }

  requestAnimationFrame(() => {
    revealObserver?.observe(element);
  });
};

const prepareRevealGroup = (container) => {
  if (!(container instanceof HTMLElement)) return;

  const matchingSelector = REVEAL_ITEM_SELECTORS.find((selector) => {
    if (selector === '.product-grid__item') return container.matches('.product-grid');
    if (selector === '.resource-list__item') return container.matches('.resource-list');
    return false;
  });
  if (!matchingSelector) return;

  const staggerCycle = container.matches('.resource-list') ? (window.innerWidth < 750 ? 2 : 4) : MAX_STAGGER_STEPS;

  [...container.querySelectorAll(`:scope > ${matchingSelector}, :scope > * ${matchingSelector}`)]
    .filter((child, index, collection) => child instanceof HTMLElement && collection.indexOf(child) === index)
    .forEach((element, index) => {
      if (element instanceof HTMLElement) prepareRevealElement(element, index % staggerCycle);
    });
};

const scanRevealTargets = (root = document) => {
  REVEAL_CONTAINER_SELECTORS.forEach((selector) => {
    getRevealTargets(root, selector).forEach((container) => {
      prepareRevealGroup(container);
    });
  });

  REVEAL_HEADER_SELECTORS.forEach((selector) => {
    getRevealTargets(root, selector).forEach((header) => {
      prepareRevealElement(header, 0);
    });
  });
};

const scheduleRevealScan = (root = document) => {
  if (scanFrame) cancelAnimationFrame(scanFrame);

  scanFrame = requestAnimationFrame(() => {
    scanFrame = null;
    scanRevealTargets(root);
  });
};

const handleIntersect = (entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;

    const element = entry.target;
    if (element instanceof HTMLElement) {
      scheduleReveal(element);
    }

    revealObserver?.unobserve(element);
  });
};

const setupRevealObserver = () => {
  if (prefersReducedMotion.matches) return;

  revealObserver?.disconnect();
  revealObserver = new IntersectionObserver(handleIntersect, {
    root: null,
    rootMargin: '0px 0px -6% 0px',
    threshold: 0.06,
  });
};

const setupMutationObserver = () => {
  if (!(document.body instanceof HTMLElement)) return;

  mutationObserver?.disconnect();

  mutationObserver = new MutationObserver((mutations) => {
    const hasAddedNodes = mutations.some((mutation) => mutation.addedNodes.length > 0);
    if (hasAddedNodes) scheduleRevealScan();
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

const initializeScrollReveal = () => {
  const isCollectionEntry = sessionStorage.getItem(COLLECTION_ENTRY_KEY) === 'true';

  if (isCollectionEntry && document.querySelector('main[data-template*="collection"]')) {
    document.documentElement.classList.add('collection-entry-navigation');
  }

  sessionStorage.removeItem(COLLECTION_ENTRY_KEY);

  setupRevealObserver();
  scanRevealTargets();
  setupMutationObserver();
  scheduleForceReveal();
  markPageReady();

  requestAnimationFrame(() => {
    document.documentElement.classList.add('scroll-reveal-active');
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeScrollReveal, { once: true });
} else {
  initializeScrollReveal();
}

document.addEventListener('shopify:section:load', () => {
  scheduleRevealScan();
  scheduleForceReveal();
});

window.addEventListener(
  'load',
  () => {
    revealVisiblePreparedElements();
  },
  { once: true }
);

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const link = target.closest('a');
  if (!shouldAnimateNavigation(link)) return;

  if (isCollectionNavigationLink(link)) {
    sessionStorage.setItem(COLLECTION_ENTRY_KEY, 'true');
  } else {
    sessionStorage.removeItem(COLLECTION_ENTRY_KEY);
  }
});

prefersReducedMotion.addEventListener('change', () => {
  if (prefersReducedMotion.matches) {
    revealObserver?.disconnect();
    revealObserver = null;
    document.querySelectorAll(`.${REVEAL_CLASS}`).forEach((element) => {
      if (element instanceof HTMLElement) markRevealed(element);
    });
    return;
  }

  setupRevealObserver();
  document.querySelectorAll(`.${REVEAL_CLASS}:not(.${REVEALED_CLASS})`).forEach((element) => {
    if (element instanceof HTMLElement) revealObserver?.observe(element);
  });
});
